/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/notificationsModule.scss';
import {AwesomeIcon, ElementPosition, Loader} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import classNames from 'classnames';

import React, {useContext, useMemo} from 'react';
import {DUTCH_DATE_FORMAT} from '../../../../../util/constants';
import {SubscriptionModuleName, SubscriptionModuleStatus} from '../../../../../util/enums';
import {SubscriptionStatus} from '../../../../../util/interfaces';
import {BolmateButton} from '../../../../controls';
import BolmateSwitch from '../../../../controls/switch/bolmateSwitch';
import {AppContext} from '../../../../misc/appContext';

interface Props {
    onToggleModule: (activate: boolean) => VoidFunction;
    loading: boolean;
    usageLastMonth: number;
}

export default function NotificationsModule(props: Props) {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {onToggleModule, loading, usageLastMonth} = props;
    const statusLabel = useMemo(() => {
        const module = user?.subscription?.getModule(SubscriptionModuleName.NOTIFICATIONS);
        if (!module) return '';
        let label: string = '';
        const futureChargeDate = module.chargePerDate.isAfter(IDate.now(), 'day');
        if (module.status === SubscriptionModuleStatus.CANCELED) return '';
        else if (module.status === SubscriptionModuleStatus.CANCELING) {
            label = `Actief tot ${module.endPerDate?.format(DUTCH_DATE_FORMAT)}, stopt daarna`;
        } else if ((module.status === SubscriptionModuleStatus.TRIAL && futureChargeDate) || futureChargeDate) {
            const endLabel = module.status === SubscriptionModuleStatus.PAID ? 'daarna actief' : 'stopt daarna';
            label = `Gratis proefperiode tot ${module.chargePerDate?.format(DUTCH_DATE_FORMAT)}, ${endLabel}`;
        } else if (module.status === SubscriptionModuleStatus.PAID) {
            label = '';
        } else if (module.status === SubscriptionModuleStatus.FREE) {
            label = 'Gratis';
        }
        return label;
    }, [user]);

    function renderBody() {
        const module = user?.subscription?.getModule(SubscriptionModuleName.NOTIFICATIONS);
        const enabled = module?.isActive([SubscriptionModuleStatus.CANCELING, SubscriptionModuleStatus.TRIAL]) ?? false;
        const subscriptionTrials = user?.subscription?.isTrialing && !user.subscription.hasPaymentMethod;
        const currentPrice = usageLastMonth < 1000 ? 5 : 10;
        return (
            <div className='body-container'>
                <label className='subscription-block-title'>Prijs bij {usageLastMonth} orders per maand</label>
                <label className='price end'>&euro;{currentPrice},-</label>
                <div className='disclaimer-container'>
                    <span>*</span>&nbsp;<label>Onder 1000 orders per maand +&euro;5,-<br/>
                    Boven 1000 orders per maand +&euro;10,-<br/>
                    Genoemde prijzen zijn excl. 21% BTW.</label>
                </div>
                <div className='checkout-signals-container'>
                    <div className='checkout-signals'>
                        <label className='end'>Ontvang meldingen van nieuwe reviews op jouw producten.</label>
                        <AwesomeIcon name='check' prefix='fas' className='check-icon'/>
                        <label className='end'>Zo ben je altijd up-to-date, zodat jij actie kunt ondernemen en geen
                            omzet verliest.</label>
                        <AwesomeIcon name='check' prefix='fas' className='check-icon'/>
                    </div>
                </div>
                <div/>
                {enabled ? <BolmateSwitch value={enabled} onChange={onToggleModule(false)} labels={['', 'AAN']}
                                          className='end'/> :
                    <BolmateButton onClick={onToggleModule(true)} label='Feature toevoegen' className='end'
                                   ornaments={{
                                       element: <AwesomeIcon name='plus'/>, placement: ElementPosition.RIGHT
                                   }} secondary={subscriptionTrials}/>}
            </div>
        );
    }

    return (
        <div className={classNames('subscription-block notifications-module',
            [undefined, SubscriptionStatus.CANCELED].includes(user?.subscription?.status) && 'inactive')}>
            <Loader active={loading}/>
            <div className='header-container'>
                <label className='header-title'>Module - Notificaties & AI Analyze</label>
                <label className='sub-title'>{statusLabel}</label>
            </div>
            {renderBody()}
        </div>
    );
}